import JSZip from 'jszip';
import { API_CONFIG } from '../config';

export interface ExportShotClip {
  id: string;
  shotNumber: number;
  shotName?: string | null;
  videoUrl?: string | null;
}

/** Sanitize a string for use as a filename (no extension). */
export function sanitizeFilename(text: string, maxLen = 80): string {
  const cleaned = (text || '')
    .trim()
    .replace(/[/\\:*?"<>|]+/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!cleaned) return 'untitled';
  return cleaned.length > maxLen ? cleaned.slice(0, maxLen) : cleaned;
}

/** Shot label for export filenames (shotName or padded shot number). */
export function shotExportLabel(shot: ExportShotClip): string {
  return shot.shotName?.trim()
    ? sanitizeFilename(shot.shotName)
    : `shot_${String(shot.shotNumber).padStart(4, '0')}`;
}

/** Per-shot export name: [shot_number]_ref_[bid_version].mp4 */
export function buildShotExportFilename(
  shot: ExportShotClip,
  bidVersion: number,
  duplicateIndex = 0
): string {
  const label = shotExportLabel(shot);
  const ver = Number.isFinite(bidVersion) ? bidVersion : 0;
  const dup = duplicateIndex > 0 ? `_${duplicateIndex + 1}` : '';
  return `${label}_ref_${ver}${dup}.mp4`;
}

/** Build unique .mp4 filenames for shots ([shot]_ref_[bidVersion].mp4). */
export function buildUniqueShotFilenames(
  shots: ExportShotClip[],
  bidVersion = 0
): Map<string, string> {
  const used = new Map<string, number>();
  const result = new Map<string, string>();

  for (const shot of shots) {
    const label = shotExportLabel(shot);
    const ver = Number.isFinite(bidVersion) ? bidVersion : 0;
    const baseKey = `${label}_ref_${ver}`.toLowerCase();
    const count = used.get(baseKey) || 0;
    used.set(baseKey, count + 1);
    result.set(shot.id, buildShotExportFilename(shot, ver, count));
  }
  return result;
}

/** Default single-file export name: project_bidName.mp4 */
export function defaultSingleFilename(projectName?: string, biddingName?: string): string {
  const project = sanitizeFilename(projectName || 'project');
  const bid = sanitizeFilename(biddingName || 'bid');
  return `${project}_${bid}.mp4`;
}

/** Resolve absolute URL for a shot videoUrl /uploads path. */
export function resolveMediaUrl(videoUrl: string): string {
  if (!videoUrl) return '';
  if (videoUrl.startsWith('http://') || videoUrl.startsWith('https://')) {
    return videoUrl;
  }
  const path = videoUrl.startsWith('/') ? videoUrl : `/${videoUrl}`;
  // Strip /app prefix if present (Docker absolute path leaked into URL)
  const clean = path.replace(/^\/app/, '');
  return `${API_CONFIG.BASE_URL}${clean}`;
}

export function supportsDirectoryPicker(): boolean {
  return typeof window !== 'undefined' && 'showDirectoryPicker' in window;
}

async function fetchClipBlob(videoUrl: string): Promise<Blob> {
  const url = resolveMediaUrl(videoUrl);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download clip (${response.status})`);
  }
  return response.blob();
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export type ProgressCallback = (done: number, total: number, label?: string) => void;

/**
 * Write each shot clip into a user-picked directory (File System Access API).
 */
export async function downloadShotFilesToDirectory(
  shots: ExportShotClip[],
  dirHandle: any,
  bidVersion = 0,
  onProgress?: ProgressCallback
): Promise<{ written: number; skipped: string[] }> {
  const withClips = shots.filter((s) => s.videoUrl);
  const skipped = shots.filter((s) => !s.videoUrl).map((s) => s.shotName || `shot ${s.shotNumber}`);
  const names = buildUniqueShotFilenames(withClips, bidVersion);
  let written = 0;

  for (let i = 0; i < withClips.length; i++) {
    const shot = withClips[i];
    const filename = names.get(shot.id)!;
    onProgress?.(i, withClips.length, filename);
    try {
      const blob = await fetchClipBlob(shot.videoUrl!);
      const fileHandle = await dirHandle.getFileHandle(filename, { create: true });
      const writable = await fileHandle.createWritable();
      await writable.write(blob);
      await writable.close();
      written++;
    } catch (e) {
      console.error(`Failed to write ${filename}`, e);
      skipped.push(filename);
    }
    onProgress?.(i + 1, withClips.length, filename);
  }

  return { written, skipped };
}

/**
 * Download each shot clip as a separate file (Chrome-friendly when folder picker is unavailable).
 */
export async function downloadShotFilesSequentially(
  shots: ExportShotClip[],
  bidVersion = 0,
  onProgress?: ProgressCallback
): Promise<{ written: number; skipped: string[] }> {
  const withClips = shots.filter((s) => s.videoUrl);
  const skipped = shots.filter((s) => !s.videoUrl).map((s) => s.shotName || `shot ${s.shotNumber}`);
  const names = buildUniqueShotFilenames(withClips, bidVersion);
  let written = 0;

  for (let i = 0; i < withClips.length; i++) {
    const shot = withClips[i];
    const filename = names.get(shot.id)!;
    onProgress?.(i, withClips.length, filename);
    try {
      const blob = await fetchClipBlob(shot.videoUrl!);
      triggerBlobDownload(blob, filename);
      written++;
      if (i < withClips.length - 1) {
        await delay(400);
      }
    } catch (e) {
      console.error(`Failed to download ${filename}`, e);
      skipped.push(filename);
    }
    onProgress?.(i + 1, withClips.length, filename);
  }

  if (written === 0) {
    throw new Error('No clips available to export');
  }

  return { written, skipped };
}

/**
 * Bundle shot clips into a ZIP and trigger browser download.
 */
export async function downloadShotFilesAsZip(
  shots: ExportShotClip[],
  zipName: string,
  bidVersion = 0,
  onProgress?: ProgressCallback
): Promise<{ written: number; skipped: string[] }> {
  const withClips = shots.filter((s) => s.videoUrl);
  const skipped = shots.filter((s) => !s.videoUrl).map((s) => s.shotName || `shot ${s.shotNumber}`);
  const names = buildUniqueShotFilenames(withClips, bidVersion);
  const zip = new JSZip();
  let written = 0;

  for (let i = 0; i < withClips.length; i++) {
    const shot = withClips[i];
    const filename = names.get(shot.id)!;
    onProgress?.(i, withClips.length, filename);
    try {
      const blob = await fetchClipBlob(shot.videoUrl!);
      zip.file(filename, blob);
      written++;
    } catch (e) {
      console.error(`Failed to add ${filename} to zip`, e);
      skipped.push(filename);
    }
    onProgress?.(i + 1, withClips.length, filename);
  }

  if (written === 0) {
    throw new Error('No clips available to export');
  }

  const content = await zip.generateAsync({ type: 'blob' });
  triggerBlobDownload(
    content,
    zipName.endsWith('.zip') ? zipName : `${zipName}.zip`
  );

  return { written, skipped };
}

/**
 * Request server-side concat and download single MP4.
 */
export async function requestSingleFileExport(
  biddingSheetId: string,
  filename: string,
  token: string
): Promise<void> {
  const safeName = sanitizeFilename(filename.replace(/\.mp4$/i, '')) + '.mp4';
  const response = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/export-video`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ mode: 'single', filename: safeName })
  });

  if (!response.ok) {
    let message = 'Export failed';
    try {
      const data = await response.json();
      message = data.error || message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = safeName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

/**
 * Ensure clips exist: call regenerate-clips if any shot is missing videoUrl.
 * Returns refreshed shot list from GET /shots (or original if regenerate skipped).
 */
export async function ensureShotClips(
  biddingSheetId: string,
  shots: ExportShotClip[],
  token: string
): Promise<ExportShotClip[]> {
  const missing = shots.filter((s) => !s.videoUrl);
  if (missing.length === 0) {
    return shots;
  }

  const regen = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/regenerate-clips`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  // 400 = no source video — keep going with whatever clips exist
  if (!regen.ok && regen.status !== 400) {
    console.warn('regenerate-clips failed', await regen.text());
  }

  const shotsRes = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/shots`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!shotsRes.ok) {
    return shots;
  }
  const data = await shotsRes.json();
  const refreshed: ExportShotClip[] = (data.shots || data || []).map((s: any) => ({
    id: s.id,
    shotNumber: s.shotNumber,
    shotName: s.shotName,
    videoUrl: s.videoUrl
  }));

  // Preserve order of original shots by id when possible
  const byId = new Map(refreshed.map((s) => [s.id, s]));
  return shots.map((s) => byId.get(s.id) || s);
}
